// src/lib/api_chatbot.ts
// Cliente para el módulo Chatbot del backend Django (Track D — D4+D5).
// Mirrors apps/chatbot/schemas.py — /admin/chatbot/**.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init, headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 409) throw new ApiError(detail || 'Conflicto.', 409);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface Paginated<T> { items: T[]; count: number }

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export type TriggerType = 'keywords' | 'regex' | 'welcome' | 'fallback' | 'intent';
export type SessionState = 'active' | 'completed' | 'abandoned' | 'handoff';
export type Channel = 'whatsapp' | 'messenger' | 'web';

export type StepType = 'message' | 'question' | 'handoff' | 'complete';

export interface FlowStep {
  type: StepType;
  body?: string;
  prompt?: string;
  var?: string;
  reason?: string;
}

export interface ChatbotFlow {
  id: number;
  org_id: string;
  name: string;
  slug: string;
  description: string;
  trigger_type: TriggerType;
  trigger_keywords: string[];
  trigger_pattern: string;
  trigger_intent: string;
  priority: number;
  channels: string[];
  steps: FlowStep[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FlowIn {
  name: string;
  slug: string;
  trigger_type?: TriggerType;
  trigger_keywords?: string[];
  trigger_pattern?: string;
  trigger_intent?: string;
  priority?: number;
  channels?: string[];
  steps?: FlowStep[];
  description?: string;
  is_active?: boolean;
}

export interface ChatbotSession {
  id: number;
  org_id: string;
  channel: Channel;
  contact_ref: string;
  flow_id: number | null;
  current_step_index: number;
  state: SessionState;
  context_data: Record<string, unknown>;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  completed_at: string | null;
  handoff_reason: string;
  created_at: string;
  updated_at: string;
}

// ═══════════════════════════════════════════════════════════════
// Flows
// ═══════════════════════════════════════════════════════════════

export async function listFlows(
  filters: {
    trigger_type?: TriggerType; is_active?: boolean; q?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<ChatbotFlow>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<ChatbotFlow>>(
    `/api/v1/admin/chatbot/flows${qs({ ...rest, limit, offset })}`,
  );
}

export async function getFlow(id: number): Promise<ChatbotFlow> {
  return djangoFetch<ChatbotFlow>(`/api/v1/admin/chatbot/flows/${id}`);
}

export async function createFlow(data: FlowIn): Promise<ChatbotFlow> {
  return djangoFetch<ChatbotFlow>('/api/v1/admin/chatbot/flows', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function updateFlow(id: number, data: Partial<FlowIn>): Promise<ChatbotFlow> {
  return djangoFetch<ChatbotFlow>(`/api/v1/admin/chatbot/flows/${id}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
}

export async function deleteFlow(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/chatbot/flows/${id}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════════
// Sessions
// ═══════════════════════════════════════════════════════════════

export async function listSessions(
  filters: {
    state?: SessionState; channel?: Channel; flow_id?: number; q?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<ChatbotSession>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<ChatbotSession>>(
    `/api/v1/admin/chatbot/sessions${qs({ ...rest, limit, offset })}`,
  );
}

export async function getSession(id: number): Promise<ChatbotSession> {
  return djangoFetch<ChatbotSession>(`/api/v1/admin/chatbot/sessions/${id}`);
}

export async function handoffSession(id: number, reason = ''): Promise<ChatbotSession> {
  return djangoFetch<ChatbotSession>(
    `/api/v1/admin/chatbot/sessions/${id}/handoff`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}
